import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { FormDataService } from '../services/form-data-service';
import { SummaryService } from '../services/summary-service';
import { getFlowFromUrl, Flow, getCleanPath } from '../utils/flow-utils';



@Injectable({ providedIn: 'root' })
export class StepGuard implements CanActivate {
  private lastFlow: Flow | null = null;
  private lastLang: string | null = null;

  constructor(
    private router: Router,
    private formData: FormDataService,
    private summaryService: SummaryService,
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    const lang = route.paramMap.get('lang') || '';
  
    if (state.url === `/${lang}/status` || state.url === '/status') return true;
  
    // ✅ Normalize URL and remove query parameters
    const fullUrl = (state.url.startsWith('/') ? state.url : `/${state.url}`).split('?')[0];
    const urlWithoutLang = fullUrl.replace(`/${lang}`, '');
    const flow = getFlowFromUrl(urlWithoutLang);
  
    const cloudMatch = urlWithoutLang.match(/cloud\/([^/]+)/);
    const cloudSubflow = cloudMatch ? cloudMatch[1] : null;
  
    if (this.lastFlow && this.lastFlow !== flow) {
      this.summaryService.clearAll();
      this.formData.clearAll();
      this.lastFlow = flow;
      this.lastLang = lang;
      return this.router.parseUrl(`/${lang}${this.getFirstStep(flow, cloudSubflow)}`);
    }
  
    if (this.lastLang && this.lastLang !== lang) {
      this.lastLang = lang;
      this.lastFlow = flow;
      return true;
    }
  
    if (!this.lastFlow) this.lastFlow = flow;
    if (!this.lastLang) this.lastLang = lang;
  
    const summary = await firstValueFrom(this.summaryService.summary$);
    const hasApps = summary.quantity > 0 && summary.totalMonthly > 0;
    const hasO365 = summary.items.some(i => i.kind === 'o365');
    const account = this.formData.getAccountData();
    const hasAccount = !!(account && Object.keys(account).length > 0);
  
    if (flow === 'apps') {
      if (urlWithoutLang.endsWith('/products')) return true;
      if (urlWithoutLang.endsWith('/account') && !hasApps)
        return this.router.parseUrl(`/${lang}/apps/products`);
      if (urlWithoutLang.endsWith('/address')) {
        if (!hasApps) return this.router.parseUrl(`/${lang}/apps/products`);
        if (!hasAccount) return this.router.parseUrl(`/${lang}/apps/account`);
      }
      return true;
    }
  
    if (flow === 'office365') {
      if (urlWithoutLang.endsWith('/products')) return true;
      if (urlWithoutLang.endsWith('/account') && !hasO365)
        return this.router.parseUrl(`/${lang}/office365/products`);
      if (urlWithoutLang.endsWith('/address')) {
        if (!hasO365) return this.router.parseUrl(`/${lang}/office365/products`);
        if (!hasAccount) return this.router.parseUrl(`/${lang}/office365/account`);
      }
      return true;
    }
  
    if (flow === 'reseller') {
      if (urlWithoutLang.endsWith('/account')) return true;
      if (urlWithoutLang.endsWith('/address') && !hasAccount)
        return this.router.parseUrl(`/${lang}/apps/reseller/account`);
      return true;
    }
  
    if (flow === 'cloud') {
      if (urlWithoutLang.match(/cloud\/[^/]+\/account$/)) return true;
      const target = cloudSubflow
        ? `/${lang}/cloud/${cloudSubflow}/account`
        : `/${lang}/cloud/account`;
      return this.router.parseUrl(target);
    }
  
    return this.router.parseUrl(`/${lang}${this.getFirstStep(flow, cloudSubflow)}`);
  }
  

  private getFirstStep(flow: Flow, subflow?: string | null): string {
    switch (flow) {
      case 'apps': return '/apps/products';
      case 'office365': return '/office365/products';
      case 'reseller': return '/apps/reseller/account';
      case 'cloud': return subflow ? `/cloud/${subflow}/account` : '/cloud/account';
      default: return '/apps/products';
    }
  }
}
